<script lang="ts">
	import SubcategoryButton from "@components/SubcategoryButton.svelte";
	import Formatted from "@components/common/Formatted.svelte";
	import StructuredData from "@components/common/StructuredData.svelte";
	import Layout from "@components/layout/Layout.svelte";
	import ProductB2bListingGrid from "@components/product/ProductB2bListingGrid.svelte";
	import ProductB2cListingGrid from "@components/product/ProductB2cListingGrid.svelte";
	import SubscriptionSection from "@components/sections/SubscriptionSection.svelte";
	import { createTitle } from "@utils/createTitle.js";
	import { getTranslate } from "@utils/getTranslate.js";
	import type { BreadcrumbList, CollectionPage, ListItem } from "schema-dts";
	import cofeeBeanIcon from "@core/assets/icons/cofee-bean.svg?raw";
	import { CategoryType } from "@core/schema/CategoryType.js";
	import { CustomerType } from "@core/schema/CustomerType.js";
	import type { SubcategoryForCustomer } from "@core/schema/Subcategory.js";
	import { appName } from "@core/schema/names/appName.js";
	import { categoryTypeNameMap } from "@core/schema/names/categoryTypeNameMap.js";
	import { getCategoryPath } from "@core/schema/paths/getCategoryPath.js";
	import { getHomepagePath } from "@core/schema/paths/getHomepagePath.js";
	import { getSubcategoryPath } from "@core/schema/paths/getSubcategoryPath.js";
	import { getCustomerType } from "@core/schema/utils/getCustomerType.js";
	import { getGlobalSettings } from "../contexts/getGlobalSettings.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import translations from "./ProductsListing.translations.json" with { type: "json" };
	import type { ProductsListingProps } from "./ProductsListingProps.js";

	const translate = getTranslate(translations);

	const {
		products,
		subcategories,
		selectedSubcategory: defaultSelectedSubategory,
		selectedCategory: defaultSelectedCategory,
		tenant,
		pricingLevel,
	}: ProductsListingProps = $props();

	const { WEB_URL } = getGlobalSettings();
	const { description, pagePath } = getGlobalTemplateProps();

	const canonicalPath = $derived(tenant.locale in pagePath ? pagePath[tenant.locale] : undefined);
	const canonicalUrl = $derived(canonicalPath !== undefined ? `${WEB_URL}${canonicalPath}` : undefined);

	const pageTitle = $derived(
		defaultSelectedSubategory?.name[tenant.locale] ?? categoryTypeNameMap[defaultSelectedCategory][tenant.locale],
	);

	const collectionPageSchema = $derived<CollectionPage>({
		"@type": "CollectionPage",
		name: pageTitle,
		description,
		...(canonicalUrl !== undefined ? { url: canonicalUrl } : {}),
	});

	const breadcrumbListSchema = $derived.by<BreadcrumbList>(() => {
		const homePath = getHomepagePath(tenant);
		const categoryPath = getCategoryPath(tenant.locale, defaultSelectedCategory);
		const categoryName = categoryTypeNameMap[defaultSelectedCategory][tenant.locale];

		const items: ListItem[] = [
			{ "@type": "ListItem", position: 1, name: appName, item: `${WEB_URL}${homePath}` },
			{ "@type": "ListItem", position: 2, name: categoryName, item: `${WEB_URL}${categoryPath}` },
		];

		if (defaultSelectedSubategory !== undefined) {
			const subcategoryPath = getSubcategoryPath(tenant.locale, defaultSelectedSubategory);
			items.push({
				"@type": "ListItem",
				position: 3,
				name: defaultSelectedSubategory.name[tenant.locale],
				item: `${WEB_URL}${subcategoryPath}`,
			});
		}

		return {
			"@type": "BreadcrumbList",
			itemListElement: items,
		};
	});

	const customerType = $derived(getCustomerType(pricingLevel));
	let selectedCategory = $derived(defaultSelectedCategory);
	let selectedSubcategory = $derived(defaultSelectedSubategory);

	const visibleSubcategories = $derived(
		subcategories.filter((subcategory) => subcategory.category === selectedCategory),
	);
	const visibleProducts = $derived(
		products.filter((product) => {
			if (selectedSubcategory) {
				return product.subcategoryId === selectedSubcategory.id;
			}
			return visibleSubcategories.some((subcategory) => subcategory.id === product.subcategoryId);
		}),
	);

	function setActiveCategory(category: CategoryType, pushToHistory = true): void {
		selectedSubcategory = undefined;
		selectedCategory = category;

		document.title = createTitle(categoryTypeNameMap[category][tenant.locale]);
		if (pushToHistory) {
			history.pushState(
				{
					selectedCategory,
				},
				"",
				getCategoryPath(tenant.locale, category),
			);
		}
	}

	function setActiveSubcategory(subcategory: SubcategoryForCustomer, pushToHistory = true): void {
		selectedCategory = subcategory.category;
		selectedSubcategory = subcategory;

		document.title = createTitle(subcategory.name[tenant.locale]);
		if (pushToHistory) {
			history.pushState(
				{
					selectedCategory,
					selectedSubcategory: subcategory.id,
				},
				"",
				getSubcategoryPath(tenant.locale, subcategory),
			);
		}
	}

	function handleHistoryPop(event: PopStateEvent): void {
		const state = event.state as {
			selectedCategory?: CategoryType;
			selectedSubcategory?: string;
		};

		if (state.selectedSubcategory) {
			const subcategory = subcategories.find((subcategory) => subcategory.id === state.selectedSubcategory);
			if (subcategory) {
				setActiveSubcategory(subcategory, false);
			}
			return;
		}
		if (state.selectedCategory !== undefined) {
			setActiveCategory(state.selectedCategory, false);
		}
	}
</script>

<svelte:window onpopstate={handleHistoryPop} />

<StructuredData data={collectionPageSchema} />
<StructuredData data={breadcrumbListSchema} />

<Layout pageHasMobileBottomBar={customerType === CustomerType.B2b}>
	<div class="grid-container full-width gap-y-6 md:gap-y-12 mt-10 md:mt-14">
		<div class="flex items-start justify-center gap-4 md:gap-12 w-full overflow-x-auto">
			{#each Object.values(CategoryType) as category (category)}
				<div class="flex flex-col items-center">
					<button
						class={[
							category === selectedCategory ? "text-fcrdarkgray" : "text-fcrgray hover:text-fcrdarkgray",
							"font-antipol text-5.5/tighter md:text-10/tighter font-medium transition-colors",
						]}
						onclick={(): void => {
							setActiveCategory(category);
						}}
					>
						<Formatted text={categoryTypeNameMap[category][tenant.locale]} />
					</button>
					{#if category === selectedCategory}
						<div class="w-5 h-1 rounded-full bg-fcrbrown"></div>
					{/if}
				</div>
			{/each}
		</div>
		{#if visibleSubcategories.length > 1}
			<div class="w-full flex items-center justify-center">
				<div class="flex items-center gap-2 text-nowrap overflow-x-auto md:flex-wrap md:justify-center p-1">
					<SubcategoryButton
						active={selectedSubcategory === undefined}
						onclick={(): void => {
							setActiveCategory(selectedCategory);
						}}
						label={translate("all")}
						icon={cofeeBeanIcon}
					/>
					{#each visibleSubcategories as subcategory (subcategory.id)}
						<SubcategoryButton
							active={selectedSubcategory?.id === subcategory.id}
							onclick={(): void => {
								setActiveSubcategory(subcategory);
							}}
							label={subcategory.name[tenant.locale]}
							icon={subcategory.icon}
						/>
					{/each}
				</div>
			</div>
		{/if}
	</div>
	<div class="my-10">
		{#if customerType === CustomerType.B2c}
			<ProductB2cListingGrid products={visibleProducts} {subcategories} />
		{:else}
			<ProductB2bListingGrid products={visibleProducts} {subcategories} />
		{/if}
	</div>
	{#if customerType === CustomerType.B2c}
		<SubscriptionSection />
	{/if}
</Layout>
